import { parseLocalDateString, getLocalDateString } from './dateUtils';
import { mergeActivePeriods } from './clientLongevityUtils';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

export const VIEW_MODES = ['all-time', 'daily', 'weekly', 'monthly', 'yearly'];

const MONTH_NAMES_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** Raw session_attendance date → YYYY-MM-DD (timezone-safe). */
export function attendanceDateToYmd(value) {
  if (value == null) return null;
  const s = String(value);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
}

export function getMonday(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d;
}

function daysBetween(start, end) {
  return Math.round((end.getTime() - start.getTime()) / MS_PER_DAY);
}

function parseDateValue(value) {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return parseLocalDateString(value);
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function maxDate(dates) {
  const valid = dates.filter(Boolean);
  if (!valid.length) return null;
  return valid.reduce((latest, current) => (current > latest ? current : latest));
}

function normalizeDate(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function endOfWeek(monday) {
  const end = new Date(monday);
  end.setDate(end.getDate() + 6);
  return end;
}

function endOfYear(year) {
  return new Date(year, 11, 31);
}

function formatDayLabel(ymd) {
  const d = parseLocalDateString(ymd);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatWeekLabel(mondayYmd) {
  const start = parseLocalDateString(mondayYmd);
  const end = endOfWeek(start);
  const startStr = `${MONTH_NAMES_SHORT[start.getMonth()]} ${start.getDate()}`;
  const endStr =
    start.getMonth() === end.getMonth()
      ? `${end.getDate()}, ${end.getFullYear()}`
      : `${MONTH_NAMES_SHORT[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
  return `${startStr} – ${endStr}`;
}

function formatMonthLabel(ym) {
  const [year, month] = ym.split('-').map(Number);
  return `${MONTH_NAMES_SHORT[month - 1]} ${year}`;
}

/** Resolve all-time mode to a concrete bucket granularity. */
export function resolveBucketMode(viewMode, startDate, endDate) {
  if (viewMode !== 'all-time') return viewMode;
  const span = daysBetween(normalizeDate(startDate), normalizeDate(endDate));
  if (span <= 60) return 'daily';
  if (span <= 730) return 'weekly';
  if (span <= 365 * 5) return 'monthly';
  return 'yearly';
}

export function getBucketKey(date, bucketMode) {
  const d = normalizeDate(date);

  if (bucketMode === 'daily') {
    const key = getLocalDateString(d);
    return { key, label: formatDayLabel(key), bucketEnd: d };
  }

  if (bucketMode === 'weekly') {
    const monday = getMonday(d);
    const key = getLocalDateString(monday);
    return { key, label: formatWeekLabel(key), bucketEnd: endOfWeek(monday) };
  }

  if (bucketMode === 'monthly') {
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    return { key, label: formatMonthLabel(key), bucketEnd: endOfMonth(d) };
  }

  const key = String(d.getFullYear());
  return { key, label: key, bucketEnd: endOfYear(d.getFullYear()) };
}

export function generateBuckets(startDate, endDate, viewMode) {
  const start = normalizeDate(startDate);
  const end = normalizeDate(endDate);
  const bucketMode = resolveBucketMode(viewMode, start, end);

  const buckets = [];
  const seen = new Set();

  let cursor;
  if (bucketMode === 'daily') {
    cursor = new Date(start);
  } else if (bucketMode === 'weekly') {
    cursor = getMonday(start);
  } else if (bucketMode === 'monthly') {
    cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  } else {
    cursor = new Date(start.getFullYear(), 0, 1);
  }

  while (cursor <= end) {
    const { key, label, bucketEnd } = getBucketKey(cursor, bucketMode);
    if (!seen.has(key)) {
      seen.add(key);
      buckets.push({ key, label, bucketEnd: normalizeDate(bucketEnd) });
    }

    if (bucketMode === 'daily') {
      cursor.setDate(cursor.getDate() + 1);
    } else if (bucketMode === 'weekly') {
      cursor.setDate(cursor.getDate() + 7);
    } else if (bucketMode === 'monthly') {
      cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
    } else {
      cursor = new Date(cursor.getFullYear() + 1, 0, 1);
    }
  }

  return { buckets, bucketMode };
}

export function isCancelledStatus(status) {
  return status === 'cancelled' || status === 'absent';
}

export function aggregateSessionCounts(records, bucketMode) {
  const counts = {};

  for (const record of records || []) {
    const ymd = attendanceDateToYmd(record?.attendance_date);
    if (!ymd) continue;

    const { key } = getBucketKey(parseLocalDateString(ymd), bucketMode);
    if (!counts[key]) {
      counts[key] = { total: 0, present: 0, cancelled: 0 };
    }

    counts[key].total += 1;
    if (record.status === 'present') {
      counts[key].present += 1;
    } else if (isCancelledStatus(record.status)) {
      counts[key].cancelled += 1;
    }
  }

  return counts;
}

function collectSessionIntervalsForClient(client, sessions, today) {
  if (!sessions.length) return [];

  const groups = new Map();

  sessions.forEach((session) => {
    const start = parseDateValue(session.valid_from);
    if (!start) return;

    const groupKey = getLocalDateString(start);
    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey).push(session);
  });

  const isActive = String(client.status || '').trim().toLowerCase() === 'active';
  const openEndedGroupKeys = [...groups.entries()]
    .filter(([, rows]) => rows.some((row) => !row.valid_until))
    .map(([groupKey]) => groupKey)
    .sort();
  const latestOpenEndedKey = openEndedGroupKeys[openEndedGroupKeys.length - 1] || null;

  const intervals = [];

  groups.forEach((rows, groupKey) => {
    const start = parseLocalDateString(groupKey);
    const explicitEnds = rows.map((row) => parseDateValue(row.valid_until)).filter(Boolean);
    const hasOpenEnded = rows.some((row) => !row.valid_until);

    if (hasOpenEnded && isActive && groupKey === latestOpenEndedKey) {
      intervals.push({ start, end: today });
      return;
    }

    if (explicitEnds.length > 0) {
      intervals.push({ start, end: maxDate(explicitEnds) });
    }
  });

  return mergeActivePeriods(intervals);
}

/** Build per-client active date ranges from session slot validity. */
export function buildClientActiveRanges(clients, sessions, today = new Date()) {
  const todayNorm = normalizeDate(today);
  const sessionsByClient = new Map();

  for (const session of sessions || []) {
    if (!session.client_id) continue;
    const key = String(session.client_id);
    if (!sessionsByClient.has(key)) {
      sessionsByClient.set(key, []);
    }
    sessionsByClient.get(key).push(session);
  }

  const ranges = new Map();

  for (const client of clients || []) {
    const clientId = String(client.id);
    const clientSessions = sessionsByClient.get(clientId) || [];
    const periods = collectSessionIntervalsForClient(client, clientSessions, todayNorm);
    if (periods.length) {
      ranges.set(clientId, periods);
    }
  }

  return ranges;
}

function isDateWithinPeriods(periods, date) {
  const d = normalizeDate(date);
  return periods.some(({ start, end }) => start <= d && d <= end);
}

export function countActiveClientsOnDate(clientRanges, date) {
  let count = 0;
  for (const periods of clientRanges.values()) {
    if (isDateWithinPeriods(periods, date)) {
      count += 1;
    }
  }
  return count;
}

export function aggregateActiveClients(clientRanges, buckets) {
  const result = {};
  for (const bucket of buckets) {
    result[bucket.key] = countActiveClientsOnDate(clientRanges, bucket.bucketEnd);
  }
  return result;
}

export function buildChartSeries(buckets, sessionCounts, activeClientsByBucket) {
  return buckets.map((bucket) => ({
    key: bucket.key,
    label: bucket.label,
    total: sessionCounts[bucket.key]?.total ?? 0,
    present: sessionCounts[bucket.key]?.present ?? 0,
    cancelled: sessionCounts[bucket.key]?.cancelled ?? 0,
    activeClients: activeClientsByBucket[bucket.key] ?? 0,
  }));
}

export function findEarliestAttendanceDate(records) {
  let earliest = null;
  for (const record of records || []) {
    const ymd = attendanceDateToYmd(record?.attendance_date);
    if (!ymd) continue;
    if (!earliest || ymd < earliest) {
      earliest = ymd;
    }
  }
  return earliest ? parseLocalDateString(earliest) : null;
}

export function getCurrentWeekBucketKey(today = new Date()) {
  const monday = getMonday(today);
  return getLocalDateString(monday);
}
